import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  MagnifyingGlass,
  UserCircle,
  PencilSimple,
  Users,
  Star,
  CalendarBlank,
  WhatsappLogo,
  Copy
} from '@phosphor-icons/react';
import EditClientModal from '../components/EditClientModal';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientList = () => {
  const { user } = useAuth();
  const hasFullPhoneAccess = canViewFullPhone(user);
  const navigate = useNavigate();
  
  const [registeredClients, setRegisteredClients] = useState([]);
  const [hasMoreRegistered, setHasMoreRegistered] = useState(true);
  const [loadingMoreRegistered, setLoadingMoreRegistered] = useState(false);
  const [inactiveClients, setInactiveClients] = useState([]);
  const [dailyActivity, setDailyActivity] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [chatSubTab, setChatSubTab] = useState('all');
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  
  const [activeTab, setActiveTab] = useState('registered'); // Default to registered
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [inactiveFilter, setInactiveFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [selectedClients, setSelectedClients] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [editingClient, setEditingClient] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset selection mode when switching tabs
    setIsSelectionMode(false);
    setSelectedClients([]);
  };
  
  // Check permission on mount
  useEffect(() => {
    const checkPermission = async () => {
      if (!user || user.role === 'super_admin') return;
      try {
        const res = await axios.get(`${API}/auth/me`);
        const powers = res.data?.user?.settings_powers || user?.settings_powers || {};
        if (powers.client_list === false || powers.client_list === 'false') {
          toast.error("Access denied: Client List power is disabled by Admin");
          navigate('/handler-dashboard');
        }
      } catch (err) {
        console.error("Permission check failed in ClientList:", err);
      }
    };
    checkPermission();
  }, [user, navigate]);

  // Load clients
  useEffect(() => {
    loadClients();
  }, [user, inactiveFilter, selectedDate]);
  
  const loadClients = async () => {
    try {
      // NEVER show loading spinner during refresh - only on mount
      // This prevents "Loading clients" from appearing
      
      // Add timestamp to prevent caching
      const timestamp = Date.now();
      
      // Load registered clients (All clients go here now - moved section removed)
      const registeredResponse = await axios.get(`${API}/clients/registered`, {
        params: { _t: timestamp, limit: 15 }
      });
      setRegisteredClients(registeredResponse.data);
      setHasMoreRegistered(registeredResponse.headers['x-has-more'] === 'true' || registeredResponse.data.length === 15);
      
      // Load inactive clients
      const inactiveResponse = await axios.get(`${API}/clients/inactive`, {
        params: {
          user_id: user.id,
          role: user.role,
          filter: inactiveFilter,
          _t: timestamp
        }
      });
      setInactiveClients(inactiveResponse.data);
      
      // Load daily activity for selected date
      const dailyResponse = await axios.get(`${API}/clients/daily-activity`, {
        params: {
          user_id: user.id,
          role: user.role,
          date: selectedDate,
          _t: timestamp
        }
      });
      
      // Combine all daily activity data
      const dailyData = dailyResponse.data;
      if (dailyData.registered_customers || dailyData.active_customers) {
        const combinedDailyActivity = [
          ...(dailyData.registered_customers || []).map(c => ({...c, activity_type: 'registered'})),
          ...(dailyData.active_customers || []).map(c => ({...c, activity_type: 'active'})),
        ];
        // Deduplicate by client id - one entry per client per day
        const seen = new Set();
        const uniqueActivity = combinedDailyActivity.filter(c => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });
        setDailyActivity(uniqueActivity);
      } else {
        setDailyActivity([]);
      }

      // Load chat rooms for Chat Monitoring
      try {
        const chatsResponse = await axios.get(`${API}/chat/rooms`, {
          params: { user_id: user.id, role: user.role, _t: timestamp }
        });
        setChatRooms(Array.isArray(chatsResponse.data) ? chatsResponse.data : []);
      } catch (e) {
        setChatRooms([]);
      }
      
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadMoreRegisteredClients = async () => {
    if (loadingMoreRegistered || !hasMoreRegistered || registeredClients.length === 0) return;
    const last = registeredClients[registeredClients.length - 1];
    if (!last?.created_at || !last?.id) {
      setHasMoreRegistered(false);
      return;
    }
    setLoadingMoreRegistered(true);
    try {
      const response = await axios.get(`${API}/clients/registered`, {
        params: {
          limit: 15,
          before_created_at: last.created_at,
          before_id: last.id,
          _t: Date.now()
        }
      });
      const next = Array.isArray(response.data) ? response.data : [];
      setRegisteredClients((current) => {
        const existing = new Set(current.map((client) => client.id));
        return [...current, ...next.filter((client) => !existing.has(client.id))];
      });
      setHasMoreRegistered(response.headers['x-has-more'] === 'true' || next.length === 15);
    } catch (error) {
      console.error('Failed to load more registered clients:', error);
    } finally {
      setLoadingMoreRegistered(false);
    }
  };

  const handleClientListScroll = (event) => {
    if (activeTab !== 'registered') return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 240) loadMoreRegisteredClients();
  };
  
  // Get current client list based on active tab
  const getCurrentClients = () => {
    let clients = [];
    switch(activeTab) {
      case 'registered': clients = registeredClients; break;
      case 'inactive': clients = inactiveClients; break;
      case 'daily': clients = dailyActivity; break;
      default: clients = registeredClients; // Default to registered
    }
    // Ensure we always return an array
    return Array.isArray(clients) ? clients : [];
  };
  
  // Filter clients based on search query
  const filteredClients = getCurrentClients().filter(client => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.client_name?.toLowerCase().includes(query) ||
      client.username?.toLowerCase().includes(query) ||
      client.mobile_number?.includes(query) ||
      client.phone?.includes(query) ||
      client.name?.toLowerCase().includes(query)
    );
  }) || [];
  
  // Selection handlers
  const toggleClientSelection = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };
  
  const selectAllClients = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };
  
  // Delete clients
  const handleDeleteClients = async () => {
    try {
      // Store IDs and count before clearing selection
      const clientIdsToDelete = [...selectedClients];
      const deleteCount = clientIdsToDelete.length;
      
      // Optimistic UI update - remove deleted clients immediately
      if (activeTab === 'registered') {
        setRegisteredClients(prev => prev.filter(c => !clientIdsToDelete.includes(c.id)));
      }
      
      // Reset selection immediately for better UX
      setIsSelectionMode(false);
      setSelectedClients([]);
      setShowDeleteConfirm(false);
      
      // Show success message
      toast.success(`${deleteCount} client${deleteCount > 1 ? 's' : ''} deleted successfully!`);
      
      // Perform actual deletion in background
      if (activeTab === 'registered') {
        await Promise.all(
          clientIdsToDelete.map(clientId => 
            axios.delete(`${API}/clients/registered/${clientId}`)
          )
        );
      }
      
      // Reload to sync with backend (in background)
      setTimeout(() => {
        loadClients();
      }, 500);
      
    } catch (error) {
      console.error('Failed to delete clients:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete clients. Please try again.';
      toast.error(errorMsg);
      // Reload to revert optimistic update on error
      loadClients();
    }
  };
  
  // Toggle favorite for registered clients
  const toggleFavorite = async (clientId, currentStatus) => {
    try {
      // Optimistic update - instant UI change
      setRegisteredClients(prev => 
        prev.map(c => c.id === clientId ? {...c, is_favorite: !currentStatus} : c)
      );
      
      await axios.put(`${API}/clients/registered/${clientId}/favorite`, {
        is_favorite: !currentStatus
      });
      
      // Reload to sync with server
      loadClients();
      
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert on error
      loadClients();
      toast.error('Failed to update favorite status');
    }
  };
  
  return (
    <div className="h-screen flex flex-col bg-wa-hover" style={{position: 'fixed', inset: 0}}>
      {/* Header */}
      <div className="bg-wa-primary-strong text-white p-4 flex-shrink-0">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard')}
              className="hover:bg-wa-primary-strong p-2 rounded-full transition-colors"
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <Users size={28} weight="bold" />
            <h1 className="text-xl font-bold">Client Management</h1>
          </div>
          
          {isSelectionMode && (
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={selectedClients.length === 0}
                className="bg-red-500 hover:bg-red-600 px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
              >
                Delete ({selectedClients.length})
              </button>
              <button
                onClick={() => {
                  setIsSelectionMode(false);
                  setSelectedClients([]);
                }}
                className="bg-wa-panel text-green-600 px-4 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto" onScroll={handleClientListScroll}>
      <div className="max-w-6xl mx-auto p-4">
        {/* Tabs */}
        <div className="bg-wa-panel rounded-lg shadow-sm mb-4">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => handleTabChange('registered')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'registered'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-green-600'
              }`}
            >
              Registered ({registeredClients.length})
            </button>
            <button
              onClick={() => handleTabChange('inactive')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'inactive'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-green-600'
              }`}
            >
              Inactive ({inactiveClients.length})
            </button>
            <button
              onClick={() => handleTabChange('daily')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'daily'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-green-600'
              }`}
            >
              Daily Activity ({dailyActivity.length})
            </button>
            <button
              onClick={() => handleTabChange('chats')}
              className={`px-6 py-3 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'chats'
                  ? 'text-green-600 border-b-2 border-green-600'
                  : 'text-wa-text-secondary hover:text-green-600'
              }`}
              data-testid="chats-monitoring-tab"
            >
              Chats ({chatRooms.length})
            </button>
          </div>
        </div>
        
        {/* Search & Actions - hidden when Chats tab */}
        {activeTab !== 'chats' && (<div className="bg-wa-panel rounded-lg shadow-sm p-4 mb-4">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="flex-1 min-w-[200px] relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-wa-text-secondary" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            {activeTab === 'inactive' && (
              <select
                value={inactiveFilter}
                onChange={(e) => setInactiveFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">All Inactive</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
              </select>
            )}
            
            {activeTab === 'daily' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            )}
            
            {(activeTab === 'moved' || activeTab === 'registered') && filteredClients.length > 0 && (
              <button
                onClick={() => setIsSelectionMode(!isSelectionMode)}
                className="px-4 py-2 bg-wa-primary-strong text-white rounded-lg font-semibold hover:bg-green-700"
              >
                {isSelectionMode ? 'Cancel Selection' : 'Select Multiple'}
              </button>
            )}
          </div>
        </div>
        )}
        
        {/* Client List - hidden when Chats tab */}
        {activeTab !== 'chats' && (
        <>
        {filteredClients.length === 0 ? (
          <div className="bg-wa-panel rounded-lg shadow-sm p-12 text-center">
            <Users size={64} weight="light" className="mx-auto text-gray-300 mb-4" />
            <p className="text-wa-text-secondary text-lg">
              {searchQuery ? 'No clients found matching your search' : `No ${activeTab} clients yet`}
            </p>
          </div>
        ) : (
          <div className="bg-wa-panel rounded-lg shadow-sm overflow-hidden">
            {isSelectionMode && (
              <div className="bg-wa-hover p-3 border-b flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedClients.length === filteredClients.length}
                    onChange={selectAllClients}
                    className="w-5 h-5 text-green-600"
                  />
                  <span className="font-semibold">Select All ({filteredClients.length})</span>
                </label>
                <span className="text-sm text-wa-text-secondary">
                  {selectedClients.length} selected
                </span>
              </div>
            )}
            
            <div className="divide-y">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="p-4 hover:bg-wa-hover transition-colors cursor-pointer"
                  onClick={async () => {
                    if (isSelectionMode) return;
                    try {
                      let chatType = 'deposit';
                      if (user?.role === 'withdrawal_handler') chatType = 'withdrawal';
                      else if (user?.role === 'support_handler') chatType = 'support';
                      else if (user?.role === 'inquiry_handler') chatType = 'inquiry';

                      const res = await axios.post(`${API}/chat/start`, {
                        customer_id: client.id,
                        chat_type: chatType
                      });
                      const roomId = res.data?.room_id || res.data?.id;
                      if (roomId) {
                        const chatPath = user.role === 'super_admin'
                          ? `/admin-chat/${roomId}`
                          : `/handler-chat/${roomId}`;
                        navigate(chatPath);
                      } else {
                        toast.error('Could not open chat');
                      }
                    } catch (err) {
                      toast.error('Could not open chat');
                    }
                  }}
                  data-testid={`client-card-${client.id}`}
                >
                  <div className="flex items-start gap-4">
                    {isSelectionMode && (
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleClientSelection(client.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1 w-5 h-5 text-green-600 cursor-pointer"
                      />
                    )}
                    
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {client.profile_pic ? (
                        <img 
                          src={client.profile_pic.startsWith('http') ? client.profile_pic : `${API}${client.profile_pic}`} 
                          alt={client.client_name || client.username} 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const parent = e.target.parentElement;
                            parent.innerHTML = `<svg viewBox="0 0 32 32" class="text-wa-text-secondary"><circle cx="16" cy="16" r="12" fill="currentColor"/><circle cx="16" cy="12" r="4" fill="white"/><path d="M8 26c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="white"/></svg>`;
                          }}
                        />
                      ) : (
                        <UserCircle size={32} weight="fill" className="text-wa-text-secondary" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h3 className="font-semibold text-wa-text-primary">
                            {client.client_name || client.username || 'Unknown'}
                          </h3>
                          <p className="text-sm text-wa-text-secondary">
                            {maskPhone(client.mobile_number || client.phone, hasFullPhoneAccess) || 'No phone'}
                          </p>
                        </div>
                        
                        <div className="flex gap-2">
                          {(activeTab === 'registered') && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingClient(client);
                                  setShowEditModal(true);
                                }}
                                className="p-2 hover:bg-wa-hover rounded-full transition-colors"
                              >
                                <PencilSimple size={20} className="text-wa-text-secondary" />
                              </button>

                              {/* WhatsApp Button */}
                              {(client.mobile_number || client.phone || client.customer_id) && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const customerId = client.customer_id || client.id;
                                    if (customerId) {
                                      try {
                                        await axios.post(`${API}/calling-reports/activity`, {
                                          customer_id: customerId,
                                          kind: 'whatsapp'
                                        });
                                        toast.success('WhatsApp redirect logged in chat');
                                      } catch (err) { console.error(err); }
                                    }
                                    const phone = (client.mobile_number || client.phone || '').replace(/\D/g, '');
                                    if (phone) {
                                      const whatsappNumber = phone.startsWith('91') ? phone : `91${phone}`;
                                      window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                                    }
                                  }}
                                  className="p-2 hover:bg-wa-hover rounded-full transition-colors cursor-pointer"
                                  title="Open WhatsApp"
                                  data-testid="client-whatsapp-btn"
                                >
                                  <WhatsappLogo size={20} className="text-green-600" />
                                </button>
                              )}

                              {/* Copy Details Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const details = [
                                    `Name: ${client.client_name || client.username || 'N/A'}`,
                                    `Phone: ${maskPhone(client.mobile_number || client.phone, hasFullPhoneAccess) || 'N/A'}`,
                                    `S/N: ${client.serial_number || 'N/A'}`,
                                    client.client_id ? `ID: ${client.client_id}` : '',
                                    client.bank_name ? `Bank: ${client.bank_name}` : '',
                                    client.account_number ? `Acc: ${client.account_number}` : '',
                                    client.ifsc_code ? `IFSC: ${client.ifsc_code}` : '',
                                  ].filter(Boolean).join('\n');
                                  navigator.clipboard.writeText(details);
                                  toast.success('Details copied!', { duration: 1500 });
                                }}
                                className="p-2 hover:bg-wa-hover rounded-full transition-colors"
                                title="Copy details"
                                data-testid="client-copy-btn"
                              >
                                <Copy size={20} className="text-blue-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {client.client_id && (
                        <p className="text-sm text-wa-text-secondary">
                          ID: {client.client_id}
                        </p>
                      )}
                      {client.serial_number && (
                        <p className="text-xs text-wa-text-secondary">
                          S/N: {client.serial_number}
                        </p>
                      )}
                      {client.same_ip_flag && (
                        <span className="inline-block mt-1 text-xs bg-wa-hover text-wa-text-primary px-2 py-0.5 rounded-full">
                          Client with Multiple Account
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {activeTab === 'registered' && (loadingMoreRegistered || hasMoreRegistered) && (
              <div className="p-4 text-center text-sm text-wa-text-secondary">
                {loadingMoreRegistered ? 'Loading more clients…' : 'Scroll for more clients'}
              </div>
            )}
          </div>
        )}
        </>
        )}

        {/* Chat Monitoring Tab */}
        {activeTab === 'chats' && (
          <div className="bg-wa-panel rounded-lg shadow-sm">
            {/* Search bar for chats */}
            <div className="p-3 border-b">
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-wa-text-secondary" />
                <input
                  type="text"
                  placeholder="Search chats..."
                  value={chatSearchQuery}
                  onChange={(e) => setChatSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  data-testid="chat-search-input"
                />
              </div>
            </div>

            {/* Sub-tabs for chat types */}
            <div className="flex overflow-x-auto border-b">
              {['all', 'deposit', 'withdrawal', 'support', 'inquiry'].map(type => {
                const filtered = type === 'all' ? chatRooms : chatRooms.filter(r => r.chat_type === type);
                const searchFiltered = chatSearchQuery
                  ? filtered.filter(r => 
                      (r.customer_name || r.display_name || '').toLowerCase().includes(chatSearchQuery.toLowerCase()) ||
                      (r.customer_phone || '').includes(chatSearchQuery)
                    )
                  : filtered;
                return (
                  <button
                    key={type}
                    onClick={() => setChatSubTab(type)}
                    className={`px-4 py-2.5 text-sm font-medium capitalize whitespace-nowrap transition-colors ${
                      chatSubTab === type
                        ? 'text-green-600 border-b-2 border-green-600'
                        : 'text-wa-text-secondary hover:text-green-600'
                    }`}
                    data-testid={`chat-sub-${type}`}
                  >
                    {type} ({searchFiltered.length})
                  </button>
                );
              })}
            </div>

            {/* Chat list */}
            <div className="divide-y">
              {(() => {
                const baseList = chatSubTab === 'all' ? chatRooms : chatRooms.filter(r => r.chat_type === chatSubTab);
                const filteredList = chatSearchQuery
                  ? baseList.filter(r => 
                      (r.customer_name || r.display_name || '').toLowerCase().includes(chatSearchQuery.toLowerCase()) ||
                      (r.customer_phone || '').includes(chatSearchQuery)
                    )
                  : baseList;
                
                return filteredList.length === 0 ? (
                  <div className="p-8 text-center text-wa-text-secondary">
                    {chatSearchQuery ? `No results for "${chatSearchQuery}"` : `No ${chatSubTab} chats found`}
                  </div>
                ) : (
                  filteredList.map(room => (
                  <div
                    key={room.id}
                    className="flex items-center gap-3 p-3 hover:bg-wa-hover cursor-pointer transition-colors"
                    onClick={() => {
                      const chatPath = user.role === 'super_admin' 
                        ? `/admin-chat/${room.id}`
                        : `/handler-chat/${room.id}`;
                      navigate(chatPath);
                    }}
                    data-testid={`chat-room-${room.id}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-wa-primary text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {(room.customer_name || room.display_name || 'C')[0]?.toUpperCase()}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <h4 className="font-semibold text-wa-text-primary text-sm truncate">
                            {room.customer_name || room.display_name || 'Customer'}
                          </h4>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase flex-shrink-0 ${
                            room.chat_type === 'deposit' ? 'bg-wa-hover text-wa-text-primary' :
                            room.chat_type === 'withdrawal' ? 'bg-wa-hover text-wa-text-primary' :
                            room.chat_type === 'support' ? 'bg-wa-hover text-wa-text-primary' :
                            'bg-wa-hover text-wa-text-primary'
                          }`}>{room.chat_type}</span>
                        </div>
                        <span className="text-[11px] text-wa-text-secondary flex-shrink-0">
                          {room.last_message_time
                            ? new Date(room.last_message_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) 
                            : ''}
                        </span>
                      </div>
                      <p className="text-xs text-wa-text-secondary truncate">
                        {room.customer_phone || 'Tap to open chat'}
                      </p>
                    </div>
                  </div>
                ))
                );
              })()}
            </div>
          </div>
        )}
      </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-wa-panel rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-wa-text-primary mb-4">Confirm Delete</h2>
            <p className="text-wa-text-secondary mb-6">
              Are you sure you want to delete {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''}? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border rounded-lg hover:bg-wa-hover font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteClients}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Client Modal */}
      {showEditModal && editingClient && (
        <EditClientModal
          client={editingClient}
          isRegistered={activeTab === 'registered'}
          userRole={user?.role}
          onClose={() => {
            setShowEditModal(false);
            setEditingClient(null);
            loadClients();
          }}
          userId={user.id}
        />
      )}
    </div>
  );
};

export default ClientList;
